/** @file evalOrder: this frame's parent-first evaluation order over effective (flex) parents. */
import type { Rig } from '@zilo/pose3d-pose/rig/rigTypes';
import type { LocalPose } from '@zilo/pose3d-pose/sample/localTypes';

/** True when walking up from `from` along `parent` reaches `target` (bounded by the node count). */
function reaches(parent: Int32Array, from: number, target: number): boolean {
  for (let k = from, steps = 0; k >= 0 && steps <= parent.length; k = parent[k] ?? -1, steps++) {
    if (k === target) return true;
  }
  return false;
}

/**
 * Effective parent = `locals[i].parent` when >= 0, else the default; an override that would close a cycle keeps the
 * default parent (decision 9). Writes the effective parents to `rig.workParent` and a topological order (ancestors
 * first, otherwise key order) to `out`.
 */
export function evalOrder(rig: Rig, locals: LocalPose, out: Int32Array): Int32Array {
  const eff = rig.workParent;
  eff.set(rig.parent);
  locals.forEach((local, i) => {
    if (local.parent < 0 || local.parent >= eff.length) return;
    if (!reaches(eff, local.parent, i)) eff[i] = local.parent;
  });
  const mark = rig.workMark;
  const stack = rig.workStack;
  mark.fill(0);
  let n = 0;
  for (let i = 0; i < eff.length; i++) {
    let depth = 0;
    for (let k = i; k >= 0 && mark[k] === 0; k = eff[k] ?? -1) {
      mark[k] = 1;
      stack[depth++] = k;
    }
    while (depth > 0) out[n++] = stack[--depth] ?? 0;
  }
  return out;
}
